import React from 'react';
import { Film, CheckCircle, Download, MessageSquare } from 'lucide-react';

/**
 * "What's new" — Additional Cuts, ADMIN/STAFF variant.
 *
 * Single screen, no steps: admin needs the shape of the change and the three
 * operational consequences (approval scope, the can't-vanish guard, and that a
 * cut is free), not a tutorial. The editor variant teaches the workflow.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

export function CutsAdminAnnouncement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-cuts-admin">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Film className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Multiple cuts on one version</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          When a client wants the same edit as more than one file — a vertical for social beside the
          horizontal, or Ceremony and Reception as separate files — editors (and you) can now attach
          every file to one version, each with a typed label, instead of spending a version slot per
          export.
        </p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What changes on a project with cuts</Eyebrow>
        <div className="space-y-1.5">
          {[[MessageSquare, 'Client review gets a cut switcher; their timestamped notes carry a tag saying which cut each note is about — still one thread per round'],
            [CheckCircle, 'Approve Draft releases every cut of that version at once — approval and revisions stay one decision per version, never per file'],
            [Download, 'After client approval, downloads and the ZIP include every cut automatically']].map(([Icon, text], i) => (
            <div key={i} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-zinc-100/75">{text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
        {[
          ['Version numbers mean revisions again', 'The old workaround — horizontal as V1, vertical as V2 — is obsolete. Don’t use it on new projects; it corrupts the revision history the cascade and release logic read.'],
          ['Cuts can’t silently vanish', 'An editor’s next version is blocked until every cut the previous version had is re-uploaded (same label) or explicitly marked unchanged. Nobody discovers a missing vertical at download time.'],
          ['A cut is free by design', 'It is a re-export of the same edit: no second pay row, due date, or status track. A different creative edit is still Double Edit or a new order.'],
          ['Labels are typed by the uploader', 'Nothing is system-generated. The label is the cut’s identity across versions, so a sloppy one ("final_v2") is worth correcting on sight.'],
        ].map(([t, d], i) => (
          <div key={i} className="flex gap-3">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
            <p className="text-[12px] text-zinc-100/75 leading-relaxed">
              <span className="text-zinc-100 font-medium">{t}.</span> {d}
            </p>
          </div>
        ))}
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default CutsAdminAnnouncement;
